import { useState } from 'react'
import {
  useAiProviders,
  useCreateAiProvider,
  useDeleteAiProvider,
  useJobs,
  usePatchAiProvider,
  useTestAiProvider,
} from '../api/hooks'
import type { AiProvider, AiProviderName, ProviderTestResult } from '../api/types'
import { AiHealthBanners } from '../components/AiHealthBanners'
import { Modal } from '../components/Modal'
import { useUi } from '../store/ui'

const PROVIDER_LABEL: Record<AiProviderName, string> = {
  anthropic: 'Anthropic (Claude)',
  openai: 'OpenAI',
  deepl: 'DeepL',
}

/** ai/worker.py's `_load_limits` defaults — no admin endpoint exposes the
 *  configured `ai_daily_request_limit` / `ai_daily_char_limit` settings
 *  values yet, so today's usage is shown against these documented defaults.
 *  TODO(backend): add a GET for the settings row so this stops guessing. */
const ASSUMED_DAILY_LIMITS = { requests: 1000, chars: 500_000 }

export function ProvidersTab() {
  const providers = useAiProviders()
  const jobs = useJobs()
  const [showCreate, setShowCreate] = useState(false)

  return (
    <div className="space-y-3">
      <AiHealthBanners jobs={jobs.data?.jobs ?? []} />

      <div className="flex items-center justify-between">
        <p className="text-xs text-ink-600">
          Bring-your-own-key translation providers. Only one enabled provider is used at a time
          (the earliest created); the rest sit idle as standby.
        </p>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          + Add provider
        </button>
      </div>

      {providers.isError && (
        <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900">
          Could not load providers: {(providers.error as Error).message}
        </p>
      )}

      <div className="space-y-2">
        {(providers.data ?? []).map((provider) => (
          <ProviderCard key={provider.id} provider={provider} />
        ))}
        {providers.isSuccess && providers.data.length === 0 && (
          <p className="rounded-lg border border-dashed border-ink-300 px-3 py-6 text-center text-xs text-ink-600">
            No AI provider configured yet — translations stay queued until one is added.
          </p>
        )}
      </div>

      {showCreate && <CreateProviderDialog onClose={() => setShowCreate(false)} />}
    </div>
  )
}

function ProviderCard({ provider }: { provider: AiProvider }) {
  const patch = usePatchAiProvider()
  const remove = useDeleteAiProvider()
  const test = useTestAiProvider()
  const pushToast = useUi((state) => state.pushToast)
  const [model, setModel] = useState(provider.model)
  const [newKey, setNewKey] = useState('')
  const [result, setResult] = useState<ProviderTestResult | null>(null)

  const modelDirty = model !== provider.model
  const requestPct = Math.min(100, Math.round((provider.usage_today.requests / ASSUMED_DAILY_LIMITS.requests) * 100))
  const charPct = Math.min(100, Math.round((provider.usage_today.chars / ASSUMED_DAILY_LIMITS.chars) * 100))

  return (
    <div className="rounded-lg border border-ink-200 bg-white p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-ink-900">{PROVIDER_LABEL[provider.provider]}</h3>
        <span
          className={`chip border ${provider.enabled ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-ink-300 bg-ink-100 text-ink-600'}`}
        >
          {provider.enabled ? 'Enabled' : 'Disabled'}
        </span>
        <div className="flex-1" />
        <label className="flex items-center gap-1.5 text-xs text-ink-700">
          <input
            type="checkbox"
            checked={provider.enabled}
            onChange={(event) =>
              patch.mutate({ providerId: provider.id, enabled: event.target.checked })
            }
          />
          Enabled
        </label>
        <button
          className="btn btn-danger"
          disabled={remove.isPending}
          onClick={() => {
            if (!window.confirm(`Remove the ${PROVIDER_LABEL[provider.provider]} provider?`)) return
            remove.mutate(provider.id)
          }}
        >
          Remove
        </button>
      </div>

      {!provider.supports_project_description && (
        <p className="mt-2 rounded border border-amber-400 bg-amber-50 px-2 py-1.5 text-2xs text-amber-900">
          <strong>{PROVIDER_LABEL[provider.provider]} ignores the per-project AI description</strong> — it
          has no prompt, only source text and formality. Client tone/context notes never reach it.
        </p>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor={`model-${provider.id}`}>
            Model
          </label>
          <div className="mt-1 flex gap-1.5">
            <input
              id={`model-${provider.id}`}
              className="field"
              value={model}
              placeholder="default"
              onChange={(event) => setModel(event.target.value)}
            />
            <button
              className="btn"
              disabled={!modelDirty || patch.isPending}
              onClick={() => patch.mutate({ providerId: provider.id, model })}
            >
              Save
            </button>
          </div>
        </div>
        <div>
          <label className="label" htmlFor={`key-${provider.id}`}>
            API key
          </label>
          <div className="mt-1 flex gap-1.5">
            <input
              id={`key-${provider.id}`}
              className="field"
              type="password"
              autoComplete="off"
              placeholder="never shown once saved — enter a new key to rotate it"
              value={newKey}
              onChange={(event) => setNewKey(event.target.value)}
            />
            <button
              className="btn"
              disabled={newKey.length < 8 || patch.isPending}
              onClick={() =>
                patch.mutate(
                  { providerId: provider.id, api_key: newKey },
                  { onSuccess: () => { setNewKey(''); pushToast('success', 'Key rotated.') } },
                )
              }
            >
              Rotate
            </button>
          </div>
        </div>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <UsageBar label="Requests today" value={provider.usage_today.requests} limit={ASSUMED_DAILY_LIMITS.requests} pct={requestPct} />
        <UsageBar label="Characters today" value={provider.usage_today.chars} limit={ASSUMED_DAILY_LIMITS.chars} pct={charPct} />
      </div>
      <p className="mt-1 text-2xs text-ink-600">
        Shown against the documented default daily budget — the server does not yet expose the
        configured limit to the UI.
      </p>

      <div className="mt-3 flex items-center gap-2">
        <button
          className="btn"
          disabled={test.isPending}
          onClick={() => {
            setResult(null)
            test.mutate(provider.id, {
              onSuccess: setResult,
              onError: (error) => pushToast('error', `Test failed: ${(error as Error).message}`),
            })
          }}
        >
          {test.isPending ? 'Testing…' : 'Test connection'}
        </button>
        {result &&
          (result.ok ? (
            <p role="status" className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-2xs text-emerald-900">
              OK — "{result.source}" → "{result.translated}" ({result.locale})
            </p>
          ) : (
            <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-2 py-1 text-2xs text-rose-900">
              Failed{result.status ? ` (HTTP ${result.status})` : ''}: {result.error}
            </p>
          ))}
      </div>
    </div>
  )
}

function UsageBar({ label, value, limit, pct }: { label: string; value: number; limit: number; pct: number }) {
  return (
    <div>
      <div className="flex items-baseline justify-between text-2xs text-ink-600">
        <span>{label}</span>
        <span className="tabular-nums">
          {value.toLocaleString()} / {limit.toLocaleString()}
        </span>
      </div>
      <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-ink-100" role="progressbar" aria-label={label} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div
          className={`h-full rounded-full ${pct >= 90 ? 'bg-rose-500' : pct >= 60 ? 'bg-amber-500' : 'bg-emerald-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

function CreateProviderDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateAiProvider()
  const [provider, setProvider] = useState<AiProviderName>('anthropic')
  const [apiKey, setApiKey] = useState('')
  const [model, setModel] = useState('')

  return (
    <Modal
      title="Add AI provider"
      description="The key is stored encrypted and never shown again once saved."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={apiKey.length < 8 || create.isPending}
            onClick={() =>
              create.mutate(
                { provider, api_key: apiKey, model: model.trim() },
                { onSuccess: onClose },
              )
            }
          >
            {create.isPending ? 'Adding…' : 'Add provider'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="label" htmlFor="new-provider-type">
            Provider
          </label>
          <select
            id="new-provider-type"
            className="field mt-1"
            value={provider}
            onChange={(event) => setProvider(event.target.value as AiProviderName)}
          >
            <option value="anthropic">Anthropic (Claude)</option>
            <option value="openai">OpenAI</option>
            <option value="deepl">DeepL</option>
          </select>
          {provider === 'deepl' && (
            <p className="mt-1 text-2xs text-amber-800">
              DeepL has no prompt — it cannot read the per-project AI description.
            </p>
          )}
        </div>
        <div>
          <label className="label" htmlFor="new-provider-key">
            API key
          </label>
          <input
            id="new-provider-key"
            className="field mt-1"
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="new-provider-model">
            Model (optional — falls back to the provider default)
          </label>
          <input
            id="new-provider-model"
            className="field mt-1"
            value={model}
            onChange={(event) => setModel(event.target.value)}
          />
        </div>
        {create.isError && (
          <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-xs text-rose-900">
            {(create.error as Error).message}
          </p>
        )}
      </div>
    </Modal>
  )
}
